<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { getUnixEndOfDay, getUnixStartOfDay } from 'helpers/DateHelper';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';

const props = defineProps({
  initialFrom: {
    type: Number,
    default: null,
  },
  initialTo: {
    type: Number,
    default: null,
  },
  initialSelectedAgents: {
    type: Array,
    default: () => [],
  },
  selectedCallCount: {
    type: Number,
    default: 0,
  },
  isDownloading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['download']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const initialFocusRef = ref(null);
const useSelectedCallsOnly = ref(false);
const useDateRange = ref(true);
const dateRange = ref([new Date(), new Date()]);
const selectedAgents = ref([]);

const agentOptions = computed(() => store.getters['agents/getAgents'] || []);
const isSubmitDisabled = computed(
  () =>
    props.isDownloading ||
    (useDateRange.value &&
      (!dateRange.value?.[0] || !dateRange.value?.[1]))
);

const toDate = timestamp => {
  if (!timestamp) {
    return new Date();
  }

  return new Date(Number(timestamp) * 1000);
};

const resetState = () => {
  useSelectedCallsOnly.value = false;
  useDateRange.value = Boolean(props.initialFrom && props.initialTo);
  dateRange.value = [toDate(props.initialFrom), toDate(props.initialTo)];
  selectedAgents.value = [...props.initialSelectedAgents];
};

const open = () => {
  resetState();
  dialogRef.value?.open();
  requestAnimationFrame(() => {
    initialFocusRef.value?.focus();
  });
};

const close = () => {
  dialogRef.value?.close();
};

const onConfirm = () => {
  emit('download', {
    useSelectedCallsOnly: useSelectedCallsOnly.value,
    from: useSelectedCallsOnly.value || !useDateRange.value
      ? null
      : getUnixStartOfDay(dateRange.value[0]),
    to: useSelectedCallsOnly.value || !useDateRange.value
      ? null
      : getUnixEndOfDay(dateRange.value[1]),
    agentIds: useSelectedCallsOnly.value
      ? []
      : selectedAgents.value.map(agent => agent.id),
  });
};

const onDateRangeChange = value => {
  dateRange.value = value;
};

onMounted(() => {
  store.dispatch('agents/get');
  resetState();
});

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    :title="t('PHONE_REPORTS.DOWNLOAD_DIALOG.TITLE')"
    :description="t('PHONE_REPORTS.DOWNLOAD_DIALOG.DESCRIPTION')"
    :confirm-button-label="t('PHONE_REPORTS.DOWNLOAD_DIALOG.CONFIRM')"
    :is-loading="isDownloading"
    :disable-confirm-button="isSubmitDisabled"
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-5">
      <div ref="initialFocusRef" tabindex="-1" />
      <div
        v-if="selectedCallCount"
        class="rounded-xl border border-n-strong bg-n-alpha-2 p-4"
      >
        <label class="flex items-start gap-3">
          <Checkbox v-model="useSelectedCallsOnly" class="mt-1" />
          <div class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{
                t('PHONE_REPORTS.DOWNLOAD_DIALOG.SELECTED_CALLS_ONLY', {
                  count: selectedCallCount,
                })
              }}
            </span>
            <span class="text-sm text-n-slate-11">
              {{ t('PHONE_REPORTS.DOWNLOAD_DIALOG.SELECTED_CALLS_HELP') }}
            </span>
          </div>
        </label>
      </div>

      <div v-if="!useSelectedCallsOnly" class="flex flex-col gap-5">
        <div class="flex flex-col gap-3">
          <label class="flex items-start gap-3">
            <Checkbox v-model="useDateRange" class="mt-1" />
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('PHONE_REPORTS.DOWNLOAD_DIALOG.USE_DATE_RANGE') }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{ t('PHONE_REPORTS.DOWNLOAD_DIALOG.USE_DATE_RANGE_HELP') }}
              </span>
            </div>
          </label>

          <WootDateRangePicker
            v-if="useDateRange"
            :append-to-body="false"
            show-range
            class="no-margin auto-width"
            :value="dateRange"
            :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
            :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
            @change="onDateRangeChange"
          />
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('PHONE_REPORTS.DOWNLOAD_DIALOG.AGENTS_LABEL') }}
          </span>
          <multiselect
            v-model="selectedAgents"
            class="no-margin"
            :options="agentOptions"
            track-by="id"
            label="name"
            multiple
            :close-on-select="false"
            :clear-on-select="false"
            hide-selected
            :placeholder="$t('CSAT_REPORTS.FILTERS.AGENTS.PLACEHOLDER')"
            selected-label
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
          />
          <span class="text-sm text-n-slate-11">
            {{ t('PHONE_REPORTS.DOWNLOAD_DIALOG.AGENTS_HELP') }}
          </span>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
:deep(.multiselect__content-wrapper) {
  @apply bg-n-surface-1 border border-n-weak text-n-slate-12 rounded-b-lg;
}

:deep(.multiselect__tags) {
  @apply bg-n-surface-1 border border-n-weak m-0 min-h-[2.875rem] pt-0 rounded-lg;
}

:deep(.multiselect__tags input[type='text']) {
  @apply bg-transparent border-n-weak placeholder:text-n-slate-11 min-h-[2.375rem] h-[2.375rem] px-0.5 py-0 text-sm;
}

:deep(.multiselect__placeholder) {
  @apply text-n-slate-11;
}

:deep(.multiselect__input) {
  @apply text-sm h-[2.375rem] mb-0 py-0 bg-transparent;
}

:deep(.multiselect__tags),
:deep(.multiselect__input),
:deep(.multiselect) {
  @apply border-n-weak text-n-slate-12 rounded-lg text-sm min-h-[2.5rem];
}

:deep(.mx-input-wrapper) {
  @apply bg-n-surface-1 border border-n-weak text-n-slate-12 rounded-lg text-sm;
}

:deep(.mx-input-wrapper input) {
  @apply border-n-weak text-sm text-n-slate-12 bg-transparent;
}

:deep(.mx-datepicker .mx-input) {
  background-color: transparent !important;
}
</style>
